// LoginPage.tsx
import { useState } from 'react';
import {
    Box,
    Button,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
    CircularProgress,
    Link,
    CssBaseline,
    Alert,
    Collapse,
    Checkbox,
    FormControlLabel,
    Container,
    useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider, styled, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, Variants } from 'framer-motion';

// Icons
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import DashboardIcon from '@mui/icons-material/Dashboard';

// Utils & Assets
import axiosInstance from '../services/axiosInstance';
import { saveAuth } from '../utils/storage';
import logoMotto from "../assets/Logo_Motto.png";

// --- 🛡️ ZOD VALIDATION ---
const loginSchema = z.object({
    loginIdentifier: z.string().min(1, "Username or Email is required"),
    password: z.string().min(1, "Password is required"),
});
type LoginFormInputs = z.infer<typeof loginSchema>;

// --- 🎨 STYLING & THEME ---
const brandColor = {
    primary: '#1A2027',
    orange: '#ffa100',
    orangeGradient: 'linear-gradient(135deg, #FF9F2F 0%, #ffa100 100%)',
    inputBg: '#F3F6F9',
    textSecondary: '#637381'
};

const theme = createTheme({
    palette: {
        primary: { main: brandColor.orange },
        text: { primary: brandColor.primary, secondary: brandColor.textSecondary },
    },
    typography: {
        fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
        h3: { fontWeight: 800, letterSpacing: '-0.5px' },
        h5: { fontWeight: 700 },
        button: { fontWeight: 700, textTransform: 'none', fontSize: '1rem' }
    },
    shape: { borderRadius: 12 },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '50px',
                    boxShadow: '0 8px 16px -4px rgba(255, 95, 0, 0.25)',
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: '24px',
                    boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.08)',
                }
            }
        }
    }
});

// 1. Main Background Wrapper
// Updated PageWrapper in LoginPage.tsx
const PageWrapper = styled(Box)({
    // Fix: Consolidate to a single display property
    display: 'flex', 
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    // THE GAP KILLER: Pin the wrapper to the absolute viewport
    position: 'fixed', 
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    margin: 0,
    padding: 0,
    overflowY: 'auto', // Allows scrolling if content is too tall
    zIndex: 1000,

    // Unified Background
    background: `
        radial-gradient(rgba(255, 255, 255, 0.2) 1.5px, transparent 1.5px),
        linear-gradient(90deg, #feab1d 0%, #ffa100 35%, #FFFFFF 80%)
    `,
    backgroundSize: '24px 24px, 100% 100%',
    backgroundRepeat: 'repeat, no-repeat',

    '@media (max-width: 900px)': {
        padding: '40px 0',
    position: 'fixed', 
        minHeight: '100vh',
        height: 'auto',
    }
});

// 2. Styled Inputs
const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: brandColor.inputBg,
        borderRadius: '12px',
        transition: 'all 0.2s ease-in-out',
        '& fieldset': { border: '1px solid transparent' },
        '&:hover fieldset': { border: `1px solid ${alpha(brandColor.orange, 0.3)}` },
        '&.Mui-focused': {
            backgroundColor: '#fff',
            boxShadow: `0 0 0 4px ${alpha(brandColor.orange, 0.1)}`,
            '& fieldset': { border: `1px solid ${brandColor.orange}` },
        },
    },
    '& .MuiInputLabel-root': {
        transform: 'translate(14px, 16px) scale(1)',
        color: brandColor.textSecondary,
        '&.Mui-focused': { color: brandColor.orange },
    }
}));

// 3. Operational Badge
const StatusBadge = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    padding: '6px 14px',
    borderRadius: '50px',
    color: 'white',
    fontWeight: 600,
    fontSize: '0.8rem',
    marginTop: '24px'
});

// --- 🎬 ANIMATION VARIANTS ---
const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 60 } }
};

const LoginPage = () => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showPassword, setShowPassword] = useState(false);
    const [generalError, setGeneralError] = useState<string | null>(null);

    const { register, handleSubmit, formState: { errors, isSubmitting }, setError } = useForm<LoginFormInputs>({
        resolver: zodResolver(loginSchema)
    });

    const onSubmit = async (data: LoginFormInputs) => {
        setGeneralError(null);
        try {
            const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.loginIdentifier);
            const payload = {
                email: isEmail ? data.loginIdentifier : null,
                userName: !isEmail ? data.loginIdentifier : null,
                password: data.password,
            };

            const response = await axiosInstance.post("/Admin/AdminLogin", payload);
            const result = response.data as any;

            if (result.responseCode === "00") {
                // 1. Destructure the raw response data
                const {
                    loginToken,
                    adminDealerLinkID,
                    adminID,
                    adminName,
                    username,
                    dealerID,
                    dealerName,
                    dealerStatusId,
                    dealerStatusDescription,
                    adminEmail,
                    passwordUpdated,
                    photoURL,
                    roleID,
                    roleName,
                } = result.responseData;

                // 2. Create the clean userDetails object
                const userDetails = {
                    adminDealerLinkID,
                    adminId: adminID,
                    name: adminName,
                    userName: username,
                    dealerId: dealerID,
                    dealerName,
                    dealerStatusId,
                    dealerStatusDescription,
                    email: adminEmail,
                    passwordUpdated: passwordUpdated || false,
                    photoURL: photoURL || '/WorkHorseWebsite/assets/images/avatar/avatar-25.webp',
                    roleID,
                    roleName,
                };

                // 3. Save auth and navigate
                saveAuth(loginToken, userDetails);
                navigate('/home');

            } else {
                setGeneralError(result.responseMessage || "Invalid credentials.");
                setError("password", { type: "manual", message: " " });
            }
        } catch (error: any) {
            setGeneralError(error.response?.data?.responseMessage || "Connection failed.");
        }
    };

    const handleGuestLogin = () => navigate('/home');

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <PageWrapper>
                {/* Constrain width so items don't spread too far */}
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center" justifyContent="center">

                        {/* --- LEFT SIDE: MARKETING CONTENT --- */}
                        {!isMobile && (
                            <Grid size={{ xs: 12, md: 6, lg: 6 }} sx={{ pr: { md: 4 } }}>
                                <motion.div initial="hidden" animate="visible" variants={containerVariants}>
                                    <Box sx={{ maxWidth: 480, color: 'white' }}>
                                        <motion.div variants={itemVariants}>
                                            <Typography variant="h3" sx={{ mb: 1.5, fontSize: { md: '2.5rem', lg: '3rem' } }}>
                                                Move Your <br />
                                                Business Forward
                                            </Typography>
                                            <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
                                                Access verified leads, market insights, <br />
                                                and dealer tools — all in one dashboard.
                                            </Typography>
                                        </motion.div>

                                        <Stack spacing={2}>
                                            {[
                                                { icon: <BarChartIcon fontSize="small" />, text: "Real-Time Market Insights" },
                                                { icon: <HeadsetMicIcon fontSize="small" />, text: "Lead & Enquiry Management" },
                                                { icon: <DashboardIcon fontSize="small" />, text: "Dealer Dashboard & Reports" }
                                            ].map((feature, idx) => (
                                                <motion.div key={idx} variants={itemVariants}>
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <Box sx={{ background: 'rgba(255,255,255,0.2)', p: 0.5, borderRadius: 1, display: 'flex' }}>
                                                            {feature.icon}
                                                        </Box>
                                                        <Typography variant="body1" fontWeight={600} fontSize="0.95rem">
                                                            {feature.text}
                                                        </Typography>
                                                    </Stack>
                                                </motion.div>
                                            ))}
                                        </Stack>

                                        <motion.div variants={itemVariants}>
                                            <StatusBadge>
                                                <CheckCircleIcon color="inherit" sx={{ fontSize: 16 }} />
                                                All systems operational
                                            </StatusBadge>
                                        </motion.div>
                                    </Box>
                                </motion.div>
                            </Grid>
                        )}

                        {/* --- RIGHT SIDE: LOGIN CARD --- */}
                        <Grid size={{ xs: 12, md: 6, lg: 5 }} sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end' }}>
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ type: 'spring', delay: 0.2, duration: 0.8 }}
                                style={{ width: '100%', maxWidth: 400 }}
                            >
                                <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, width: '100%', borderRadius: '24px' }}>

                                    {/* Logo */}
                                    <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                                        <img src={logoMotto} alt="WorkHorse" style={{ height: 35 }} />
                                    </Box>

                                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                                        <Typography variant="h5" sx={{ mb: 0.5, color: '#1A2027' }}>
                                            Welcome back
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Sign in to your WorkHorse Admin Portal
                                        </Typography>
                                    </Box>

                                    <Collapse in={!!generalError}>
                                        <Alert severity="error" sx={{ mb: 2, fontSize: '0.875rem' }}>{generalError}</Alert>
                                    </Collapse>

                                    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: '#333', fontSize: '0.85rem' }}>
                                            Email or Username
                                        </Typography>
                                        <StyledTextField
                                            fullWidth
                                            size="small"
                                            placeholder="Enter your email"
                                            id="loginIdentifier"
                                            disabled={isSubmitting}
                                            {...register("loginIdentifier")}
                                            error={!!errors.loginIdentifier}
                                            helperText={errors.loginIdentifier?.message}
                                            sx={{ mb: 2 }}
                                            InputProps={{ disableUnderline: true, sx: { height: 45 } }}
                                        />

                                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: '#333', fontSize: '0.85rem' }}>
                                            Password
                                        </Typography>
                                        <StyledTextField
                                            fullWidth
                                            size="small"
                                            placeholder="Enter password"
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            disabled={isSubmitting}
                                            {...register("password")}
                                            error={!!errors.password}
                                            helperText={errors.password?.message}
                                            sx={{ mb: 1 }}
                                            InputProps={{
                                                disableUnderline: true,
                                                sx: { height: 45 },
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />

                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                            <FormControlLabel
                                                control={<Checkbox size="small" sx={{ p: 0.5, ml: 0.5, color: brandColor.orange, '&.Mui-checked': { color: brandColor.orange } }} />}
                                                label={<Typography variant="body2" color="text.secondary" fontSize="0.85rem">Remember me</Typography>}
                                            />
                                        </Stack>

                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            disabled={isSubmitting}
                                            sx={{
                                                height: 45,
                                                background: brandColor.orangeGradient,
                                                '&:hover': { background: 'linear-gradient(135deg, #F08800 0%, ##ffa100 100%)', color: 'white' },
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
                                        </Button>
                                    </Box>

                                    {/* Footer Area */}
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, pt: 2, borderTop: '1px solid #f0f0f0' }}>
                                        <Link
                                            href="https://portal.workhorse.com.sg/auth/jwt/reset-password"
                                            underline="hover"
                                            target="_blank"
                                            sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8rem' }}
                                        >
                                            Forgot password?
                                        </Link>

                                        <Button
                                            onClick={handleGuestLogin}
                                            variant="outlined"
                                            color="inherit"
                                            size="small"
                                            sx={{
                                                borderColor: '#E0E0E0',
                                                color: 'text.secondary',
                                                borderRadius: '8px',
                                                textTransform: 'none',
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            Guest Access &gt;
                                        </Button>
                                    </Stack>

                                    <Typography variant="caption" align="center" sx={{ display: 'block', mt: 3, color: '#BBB' }}>
                                        © 2025-2026 WorkHorse · v1.0
                                    </Typography>
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </PageWrapper>
        </ThemeProvider>
    );
};

export default LoginPage;